// @mui
import { Box, Card, CardHeader, Divider, Skeleton, Button, Stack, useMediaQuery } from '@mui/material';
// utils
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay, DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
//-----------------------------------------------------
import { HttpErrorHandler } from '../../utils/ErrorHandler';
import Iconify from '../../components/iconify';
import API from '../../api';

/**
 * - isAdmin - ruolo di utente: Admin / Utente
 * @param {boolean} isAdmin
 * @param {any} props
 * @returns Dashboard Calendar Card UI
 */

const GenerateAPIDays = ({ isAdmin, ...props }) => {
  const navigate = useNavigate();
  const is400Screen = useMediaQuery('(min-width: 400px)');
  const { isApiCalled, highlightedDays = [], day, outsideCurrentMonth, ...other } = props;
  const isHighlighted = highlightedDays.includes(day.date());
  const occurrences = highlightedDays.filter((highlightedDay) => highlightedDay === day.date());

  // handle calendar event click operations
  const handleDayClicked = (day) => {
    const _day = new Date(day);
    navigate(isAdmin ? '/admin/events' : '/user/events', { state: { requestDay: _day } });
  };

  // Generates date picker UI
  return isApiCalled ? (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      sx={{ backgroundColor: isHighlighted ? '#d1f8eb' : 'transparent', position: 'relative' }}
      onClick={() => handleDayClicked(day)}
    >
      {day.date()}
      {isHighlighted && (
        <Box
          sx={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: dayjs(day).format('DD/MM/YYYY') === dayjs().format('DD/MM/YYYY') ? '#007f55' : '#00a56e',
            position: 'absolute',
            top: is400Screen ? '-6px' : '-8px',
            right: is400Screen ? '-6px' : '-8px',
            fontSize: '90%',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: '2',
          }}
        >
          {occurrences.length < 10 ? occurrences.length : '9+'}
        </Box>
      )}
    </PickersDay>
  ) : (
    // loading data skeleton UI
    <Skeleton
      animation="wave"
      variant="circular"
      width={is400Screen ? '42px' : '32px'}
      height={is400Screen ? '42px' : '32px'}
    />
  );
};

/**
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns in-between dates
 */
const getInBetweenDates = (startDate, endDate) => {
  const dates = [];
  let currentDate = startDate;
  const addDays = function (days) {
    const date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
  };
  while (currentDate <= endDate) {
    dates.push(currentDate);
    currentDate = addDays.call(currentDate, 1);
  }
  return dates;
};

/**
 * @param {string} title
 * @param {boolean} isAdmin
 * @returns Dashboard Calendar UI card
 */
export default function AppCalendar({ title, isAdmin }) {
  const [highlightedDaysByMonth, setHighlightedDaysByMonth] = useState({});
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [events, setEvents] = useState([]);
  const [highlightedDaysData, setHighlightedDaysData] = useState([]);
  const [isApiCalled, setIsApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Get Calendar event dates from API
  useEffect(() => {
    getEvents();
  }, []);

  // Insert Calendars Event Dates Into HighlightedDaysData
  useEffect(() => {
    const newHighlightedDaysData = [];

    events.forEach((event) => {
      const dates = getInBetweenDates(
        new Date(dayjs(event.start_date).format('YYYY,MM,DD')),
        new Date(dayjs(event.end_date).format('YYYY,MM,DD'))
      );

      dates.forEach((date) => {
        const dateMonth = dayjs(date).format('YYYY-MM-DD').slice(0, 7);
        const dateDay = dayjs(date).format('YYYY-MM-DD').slice(8, 10);

        const foundMonth = newHighlightedDaysData.find((month) => month.month === dateMonth);

        if (foundMonth) {
          foundMonth.days.push(Number(dateDay));
        } else {
          const highlightedMonth = {
            month: dateMonth,
            days: [],
          };
          highlightedMonth.days.push(Number(dateDay));
          newHighlightedDaysData.push(highlightedMonth);
        }
      });
    });

    setHighlightedDaysData(newHighlightedDaysData);
  }, [events]);

  const getEvents = async () => {
    try {
      const [celebrationsRes, eventsRes] = await Promise.all([
        API.get('/celebrations?dashboard'),
        API.get('/events?dashboard'),
      ]);

      const combinedData = [...celebrationsRes.data, ...eventsRes.data];

      setEvents(combinedData);
      setIsApiCalled(true);
    } catch (error) {
      HttpErrorHandler(error, setErrorMessage);
    }
  };

  // Highlight days in calendar UI
  useEffect(() => {
    const highlightedDays = {};
    highlightedDaysData.forEach((data) => {
      highlightedDays[data.month] = data.days;
    });

    setHighlightedDaysByMonth(highlightedDays);
  }, [highlightedDaysData, events]);

  // handle month change request
  const handleMonthChange = (date) => {
    setCurrentDate(currentDate.year(date.year()).month(date.month()));
  };

  return (
    <Card sx={{ height: '460px', display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
      <CardHeader title={title} />
      <Box width="93%" sx={{ margin: '0 auto' }}>
        <Box dir="ltr">
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DateCalendar
              sx={{ textTransform: 'capitalize' }}
              views={['day']}
              value={selectedDate}
              onChange={(newDate) => setCurrentDate(newDate)}
              onMonthChange={handleMonthChange}
              slots={{
                day: (props) => <GenerateAPIDays {...props} isApiCalled={isApiCalled} isAdmin={isAdmin} />,
              }}
              slotProps={{
                day: {
                  highlightedDays: highlightedDaysByMonth[currentDate.format('YYYY-MM')] || [],
                },
                textField: { fullWidth: true },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      {/* <Divider sx={{ width: '100%' }} />

      <Stack
        direction="row"
        justifyContent="end"
        alignItems="center"
        alignSelf="end"
        sx={{ paddingRight: '24px', minHeight: '50px' }}
      >
        <Button
          href={isAdmin ? '/admin/calendar' : '/user/calendar'}
          size="small"
          color="inherit"
          endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
          sx={{ textTransform: 'none' }}
        >
          Vai al calendario
        </Button>
      </Stack> */}
    </Card>
  );
}
